import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useThrottleFn } from 'ahooks'
import { NSClass } from 'gga-types'
import {
  ICoursewareModalContexts,
  useCoursewareModal,
} from '@/hooks/myPages/CoursewareModalProvider'
import { VideoUploadForm } from '@/components/Form/Courseware/VideoUploadForm'
import { submitPtHomework, submitWorkApiV2 } from '@/services/class'
import { PDFUploadForm } from '@/components/Form/Courseware/PDFUploadForm'
import useRawContent from '@/hooks/useRawContent'
import ReplayCodeUploadForm from '@/components/Form/Courseware/ReplayCodeUploadForm'
import ReplayFileUploadForm from '@/components/Form/Courseware/ReplayFileUploadForm'

type ISoloTaskUploadProps = {
  header?: ReactNode
  classId: number
  nodeId: number
  onSuccess: (record: NSClass.RecordType) => void
  submissionStatus?: NSClass.SubmissionStatus
  ctx?: ICoursewareModalContexts
}

export const SoloTaskUpload = ({
  header,
  classId,
  onSuccess,
  nodeId,
  submissionStatus,
  ctx = 'CLASS_MODAL_CONTEXT',
}: ISoloTaskUploadProps) => {
  const { courseware, requestClose, setBeforeClose, isGGAPro } =
    useCoursewareModal(ctx)

  const { parsed, renderReplacedVideo } = useRawContent({
    content: courseware.content || '',
  })

  const [submitting, setSubmitting] = useState(false)

  // 2026-09 이관: 업로드가 전부 S3 로 가므로 언어와 무관하게 `aws` 입니다.
  //   예전에는 한국만 S3 였고 나머지는 알리바바 OSS 라 언어로 갈랐습니다.
  const platform = 'aws'

  const _onSubmit = useCallback(
    async data => {
      // Build POST Body Data
      setSubmitting(true)
      const postData = {
        description: data.description,
        content: data.url,
        file_path: data.file_name,
        replay_code: data.replay_code,
        replay_file_path: data.replay_file_path,
        file_upload_platform: data.file_upload_platform || platform,
        file_size: data.file_size,
        player_identity: data.player_identity,
      }

      try {
        const req = isGGAPro ? submitPtHomework : submitWorkApiV2
        const res = await req(classId, nodeId, postData)

        if (res && res.data && res.data.data) {
          onSuccess(res.data.data)
        }

        setSubmitting(false)
      } catch (e) {
        console.error(e)
        setSubmitting(false)
      }
    },
    [classId, isGGAPro, nodeId, onSuccess, platform]
  )
  const { run: onSubmit } = useThrottleFn(_onSubmit, { wait: 1000 })

  // 提交 SoloTask 作业
  const submissionType = useMemo(() => {
    return courseware.syllabus.submit_type
  }, [courseware])

  useEffect(() => {
    renderReplacedVideo()
  }, [renderReplacedVideo])

  return (
    <div
      className={
        "overflow-auto bg-repeat bg-[url('https://res.ggacademy.gg/user-web/images/bg-faq.png')]"
      }
    >
      <div className={'max-w-screen-xl mx-auto bg-white bg-opacity-60 p-2'}>
        {header}
        <div
          className="w-full gga-page-view mb-12"
          dangerouslySetInnerHTML={{ __html: parsed }}
        />
        {submissionType === 'video' && (
          <VideoUploadForm
            className="pb-8"
            submissionStatus={submissionStatus}
            submitting={submitting}
            requestClose={requestClose}
            onSubmit={onSubmit}
            onModalClose={setBeforeClose}
          />
        )}
        {submissionType === 'replay_code' && (
          <ReplayCodeUploadForm
            className="pb-8"
            submissionStatus={submissionStatus}
            onSubmit={onSubmit}
            onModalClose={setBeforeClose}
          />
        )}
        {submissionType === 'replay_file' && (
          <ReplayFileUploadForm
            className="pb-8"
            submissionStatus={submissionStatus}
            onSubmit={onSubmit}
            onModalClose={setBeforeClose}
          />
        )}

        {submissionType === 'pdf' && (
          <PDFUploadForm
            className="pb-8"
            submitting={submitting}
            requestClose={requestClose}
            onSubmit={onSubmit}
            onModalClose={setBeforeClose}
          />
        )}
      </div>
    </div>
  )
}
